import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Target, Flame, TrendingUp, Clock, ArrowRight, Layers } from "lucide-react";
import TopNav from "@/components/TopNav";
import DiagnosticScores from "@/components/DiagnosticScores";
import SecurityQuestionsBanner from "@/components/SecurityQuestionsBanner";

export default function Dashboard() {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [homework, setHomework] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0, studied_today: false });

  useEffect(() => {
    api.get("/progress").then((r) => setProgress(r.data));
    api.get("/homework").then((r) => setHomework(r.data.items || []));
    api.get("/subjects").then((r) =>
      // Hide the "math" pseudo-subject — Math Lab has its own dedicated
      // sidebar entry and shouldn't clutter the subject grid.
      setSubjects((r.data || []).filter((s) => s.id !== "math"))
    );
    api.get("/study/streak").then((r) => setStreak(r.data));
  }, []);

  const stats = progress?.stats || {};

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="-mx-6 -mt-6 mb-2">
        <TopNav />
      </div>
      <SecurityQuestionsBanner userEmail={user?.email} />
      <div>
        <div className="text-sm uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--text-muted)" }}>
          {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold" data-testid="dashboard-greeting">
          {(() => {
            const first = (user?.name || "").trim().split(/\s+/)[0];
            return first ? `Hi, ${first}.` : "Hi there!";
          })()}
        </h1>
        <p className="mt-2 text-lg" style={{ color: "var(--text-secondary)" }}>
          {homework.length > 0 ? `You have ${homework.length} topic${homework.length > 1 ? "s" : ""} to review today.` : "Ready to dive in?"}
        </p>
      </div>

      {/* Streak / daily nudge banner */}
      <div
        className="pm-card p-5 flex items-center justify-between grain gap-4 flex-wrap"
        style={{ background: streak.studied_today ? "var(--hl-green)" : "var(--hl-yellow)" }}
        data-testid="streak-banner"
      >
        <div className="flex items-center gap-4">
          <div className="text-4xl">{streak.tier?.emoji || (streak.current_streak >= 3 ? "🔥" : "🌱")}</div>
          <div>
            <div className="font-display font-bold text-xl inline-flex items-center gap-2" data-testid="streak-count">
              {streak.current_streak}-day streak
              {streak.tier && (
                <span
                  className="pm-badge text-xs"
                  style={{ background: `${streak.tier.color}22`, color: streak.tier.color, borderColor: `${streak.tier.color}55` }}
                  data-testid="streak-tier-badge"
                >
                  {streak.tier.name}
                </span>
              )}
            </div>
            <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {streak.studied_today
                ? streak.next_tier
                  ? `${streak.next_tier.tier - streak.current_streak} more day${streak.next_tier.tier - streak.current_streak === 1 ? "" : "s"} to unlock ${streak.next_tier.name}.`
                  : "You've hit the top tier. Absolute legend."
                : "You haven't studied today yet — a 5-minute flashcard session keeps your streak alive."}
            </div>
          </div>
        </div>
        <Link to="/app/flashcards" className="pm-btn-primary text-sm flex items-center gap-2 shrink-0" data-testid="streak-cta-btn">
          <Layers size={14} /> {streak.studied_today ? "Review more" : "Study now"}
        </Link>
      </div>

      {/* Stats bento */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
        {[
          { icon: Target, label: "Overall accuracy", value: `${Math.round((stats.overall_accuracy || 0) * 100)}%`, color: "var(--primary)" },
          { icon: Flame, label: "Attempts", value: stats.total_attempts || 0, color: "var(--accent)" },
          { icon: TrendingUp, label: "Topics touched", value: stats.topics_touched || 0, color: "var(--mastery)" },
          { icon: Clock, label: "Homework due", value: homework.length, color: "var(--weakness)" },
        ].map((s, i) => (
          <div key={i} className="pm-card p-5" data-testid={`stat-${i}`}>
            <s.icon size={18} style={{ color: s.color }} />
            <div className="font-display text-3xl font-bold mt-3">{s.value}</div>
            <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Diagnostic Scores (Pro-only) */}
      <DiagnosticScores />

      {/* Homework */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-bold">Homework queue</h2>
          <Link to="/app/progress" className="text-sm underline" style={{ color: "var(--primary)" }} data-testid="dash-see-progress">
            See full progress →
          </Link>
        </div>
        {homework.length === 0 ? (
          <div className="pm-card p-6 text-sm" style={{ color: "var(--text-secondary)" }}>
            No reviews due yet. Answer some questions to build your spaced-repetition schedule.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="homework-list">
            {homework.slice(0, 4).map((h, i) => (
              <Link
                key={i}
                to={`/app/topic/${h.topic.id}`}
                className="pm-card pm-card-hover p-5 flex items-center justify-between"
                data-testid={`homework-item-${i}`}
              >
                <div>
                  <div className="pm-badge mb-2" style={{ background: h.accuracy < 0.5 ? "#fdecea" : "var(--hl-yellow)", color: h.accuracy < 0.5 ? "var(--weakness)" : "var(--text)" }}>
                    {h.accuracy < 0.5 ? "Weak" : "Review"}
                  </div>
                  <div className="font-display font-bold text-lg">{h.topic.name}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                    Accuracy: {Math.round(h.accuracy * 100)}%
                  </div>
                </div>
                <ArrowRight size={18} style={{ color: "var(--text-muted)" }} />
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Subjects */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-bold">Explore subjects</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="subject-grid">
          {subjects.map((s) => (
            <Link
              key={s.id}
              to={`/app/subjects/${s.id}`}
              className="pm-card pm-card-hover p-5"
              data-testid={`subject-card-${s.id}`}
            >
              <div className="text-3xl mb-3">{s.emoji}</div>
              <div className="font-display font-bold text-lg leading-tight">{s.name}</div>
              <div className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>{s.topic_count} topics</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
